import { TooltipProps } from '@mui/material';
import cx from 'classnames';
import { Button } from 'components/admin/ui';
import { PermissionName, useAuthContext } from 'components/auth';
import { Tooltip } from 'components/ui';
import { XClose } from 'ethena-icons';
import { FE_URL_PATHS } from 'lib/constants';
import {
  createUserInteraction,
  UserInteraction,
  useUserInteraction,
} from 'lib/tracking';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useOnboardingContext } from './hooks';
import css from './OnboardingTooltip.module.scss';
import { OnboardingTooltipType } from './types';

interface IOnboardingTooltipProps
  extends Omit<
    TooltipProps,
    'componentsProps' | 'disableHoverListener' | 'open' | 'title'
  > {
  description: string;
  isHidden?: boolean;
  onPrimaryButtonClick?: () => void;
  primaryButtonLabel: string;
  showCloseButton?: boolean;
  title: string;
  tooltipType: OnboardingTooltipType;
}

const OnboardingTooltip = ({
  children,
  description,
  isHidden = false,
  onPrimaryButtonClick,
  primaryButtonLabel,
  title,
  tooltipType,
  ...props
}: IOnboardingTooltipProps) => {
  const { getIsTooltipActive } = useOnboardingContext();
  const { getIsInPermission } = useAuthContext();

  const isOpenTimeoutRef = useRef<NodeJS.Timeout>();

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const {
    data: interactions,
    isLoading,
    mutate: refreshInteractions,
  } = useUserInteraction(UserInteraction.VIEWED_ONBOARDING_TOOLTIP, {
    shouldFetch: getIsInPermission(PermissionName.COMPANY_ONBOARDING),
  });

  const handlePrimaryButtonClick = async () => {
    await setHasViewedTooltip();
    onPrimaryButtonClick?.();
  };

  const hideTooltip = () => {
    resetIsOpenTimeout();
    setIsOpen(false);
  };

  const resetIsOpenTimeout = () => {
    clearTimeout(isOpenTimeoutRef.current);
    isOpenTimeoutRef.current = undefined;
  };

  const setHasViewedTooltip = async () => {
    hideTooltip();
    await createUserInteraction('', {
      interaction: UserInteraction.VIEWED_ONBOARDING_TOOLTIP,
      interactionValue: tooltipType,
    });
    refreshInteractions();
  };

  const showTooltip = (delay: number = 1000) => {
    if (!isOpenTimeoutRef.current) {
      isOpenTimeoutRef.current = setTimeout(() => {
        setIsOpen(true);
        resetIsOpenTimeout();
      }, delay);
    }
  };

  const hasViewedTooltip = _.some(
    interactions,
    (i) => i.interactionValue === tooltipType
  );

  const shouldDisplayTooltip =
    !isLoading && !hasViewedTooltip && getIsTooltipActive(tooltipType);

  // Open tooltip when it initially becomes active.
  useEffect(() => {
    if (!isInitialized && shouldDisplayTooltip) {
      showTooltip();
      setIsInitialized(true);
    }
  }, [isInitialized, shouldDisplayTooltip]);

  // Hide/show the tooltip depending on the `isHidden` prop.
  useEffect(() => {
    if (isInitialized && shouldDisplayTooltip) {
      if (isHidden) {
        hideTooltip();
      } else {
        showTooltip(250);
      }
    }
  }, [isHidden, isInitialized, shouldDisplayTooltip]);

  const body = (
    <>
      <div className="d-flex justify-content-between mb-2">
        <div className={css.title}>{title}</div>
        <button
          aria-label="Close"
          className={css.closeButton}
          onClick={setHasViewedTooltip}
        >
          <XClose height={20} width={20} />
        </button>
      </div>
      {description && <div className={css.description}>{description}</div>}
      <Button className={css.primaryButton} onClick={handlePrimaryButtonClick}>
        {primaryButtonLabel}
      </Button>
    </>
  );

  return (
    <Tooltip
      {...props}
      childrenClassName={
        shouldDisplayTooltip &&
        cx(css.children, {
          [css.hidden]: !isOpen,
        })
      }
      componentsProps={{
        arrow: {
          className: css.arrow,
        },
        tooltip: {
          className: css.tooltip,
        },
      }}
      disableHoverListener
      open={shouldDisplayTooltip && isOpen}
      title={body}
    >
      {children}
    </Tooltip>
  );
};

interface IOnboardingTooltipImplProps {
  children: ReactElement;
  isHidden?: boolean;
}

export const HelpOnboardingTooltip = ({
  children,
  isHidden = false,
}: IOnboardingTooltipImplProps) => (
  <OnboardingTooltip
    description="Access FAQs and resources at any time."
    isHidden={isHidden}
    placement="right"
    primaryButtonLabel="Got it!"
    title="Help is Always Here"
    tooltipType={OnboardingTooltipType.HELP}
  >
    {children}
  </OnboardingTooltip>
);

export const ScheduleAssignmentOnboardingTooltip = ({
  children,
  isHidden = false,
}: IOnboardingTooltipImplProps) => {
  const { push } = useRouter();

  const handlePrimaryButtonClick = () => {
    push(`${FE_URL_PATHS.ADMIN_TRAINING_CAMPAIGNS}/create`);
  };

  return (
    <OnboardingTooltip
      description="Now, it’s time to assign training to your team members."
      isHidden={isHidden}
      onPrimaryButtonClick={handlePrimaryButtonClick}
      placement="right"
      primaryButtonLabel="Add Training"
      title="Add Training"
      tooltipType={OnboardingTooltipType.SCHEDULE_ASSIGNMENT}
    >
      {children}
    </OnboardingTooltip>
  );
};
